/**
 * Detect stale SPA chunk / dynamic-import failures after a deploy.
 * Used to trigger a one-shot page reload instead of a full error screen.
 */

const CHUNK_RELOAD_KEY = 'hf_chunk_reload'
/** Avoid infinite reload loops if assets are still missing after a reload. */
const RELOAD_COOLDOWN_MS = 15_000

const CHUNK_LOAD_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /failed to load module script/i,
  /mime type of ["']?text\/html/i,
  /loading chunk [\w-]+ failed/i,
  /loading CSS chunk [\w-]+ failed/i,
  /ChunkLoadError/i,
]

/**
 * @param {unknown} errorOrEvent
 * @returns {boolean}
 */
export function isChunkLoadError(errorOrEvent) {
  if (!errorOrEvent) return false

  const candidates = []

  if (typeof errorOrEvent === 'string') {
    candidates.push(errorOrEvent)
  }

  const error = /** @type {any} */ (errorOrEvent)
  if (typeof error?.message === 'string') candidates.push(error.message)
  if (typeof error?.name === 'string') candidates.push(error.name)
  if (typeof error?.error?.message === 'string')
    candidates.push(error.error.message)
  if (typeof error?.error?.name === 'string') candidates.push(error.error.name)
  if (typeof error?.reason === 'string') candidates.push(error.reason)
  if (typeof error?.reason?.message === 'string')
    candidates.push(error.reason.message)
  if (typeof error?.reason?.name === 'string') candidates.push(error.reason.name)

  // Resource/module load failures often have an empty message; inspect the target.
  const target = error?.target
  if (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
    const src = target.src || target.href || ''
    if (
      /\/assets\//.test(src) ||
      /\.[a-f0-9]{8,}\.(js|css|mjs)(\?|$)/i.test(src)
    ) {
      return true
    }
  }

  return candidates.some((text) =>
    CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(text)),
  )
}

/**
 * Reload once after a deploy-stale chunk failure.
 * Returns true if a reload was started (caller should stop other error UI).
 * Returns false if a reload already happened recently (fall through to error UI).
 *
 * @returns {boolean}
 */
export function reloadOnceForChunkError() {
  try {
    if (typeof sessionStorage === 'undefined') {
      window.location.reload()
      return true
    }
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0)
    if (last && Date.now() - last < RELOAD_COOLDOWN_MS) {
      return false
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
    window.location.reload()
    return true
  } catch (_e) {
    window.location.reload()
    return true
  }
}

export { CHUNK_RELOAD_KEY, RELOAD_COOLDOWN_MS }
